<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useCurrentAgentPause } from 'dashboard/composables/useCurrentAgentPause';
import { useI18n } from 'vue-i18n';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');
const {
  pauseState,
  isPauseStateLoading,
  isPauseActionLoading,
  requestPause,
  stopPause,
} = useCurrentAgentPause();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

const formatDateTime = isoString => {
  if (!isoString) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(isoString));
};
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <div class="grid gap-0">
      <DropdownItem preserve-open class="gap-1">
        <div class="flex-grow flex items-center gap-1 min-w-0">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <DropdownContainer class="shrink-0">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="toggle"
            >
              <div class="flex gap-1 items-center min-w-0 text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span class="truncate max-w-[7rem]">
                  {{ activeStatus.label }}
                </span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem>
        <div class="flex-grow min-w-0">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          <Icon
            v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
            icon="i-lucide-info"
            class="inline-block align-middle ms-1 size-4 text-n-slate-10"
          />
        </div>
        <ToggleSwitch v-model="autoOfflineToggle" />
      </DropdownItem>
      <DropdownItem preserve-open class="items-start">
        <div class="flex w-full flex-col gap-3">
          <div class="text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_MGMT.PAUSE_SELF.TITLE') }}
          </div>

          <div v-if="isPauseStateLoading" class="text-sm text-n-slate-11">
            {{ $t('AGENT_MGMT.PAUSE_SELF.LOADING') }}
          </div>

          <div
            v-else-if="pauseState.active_pause"
            class="rounded-xl border border-n-weak p-3"
          >
            <div class="text-sm font-medium text-n-slate-12">
              {{ pauseState.active_pause.label }}
            </div>
            <div class="mt-1 text-sm text-n-slate-11">
              {{
                $t('AGENT_MGMT.PAUSE_SELF.ACTIVE_UNTIL', {
                  time: formatDateTime(pauseState.active_pause.ends_at),
                })
              }}
            </div>
            <Button
              class="mt-3"
              size="sm"
              slate
              :label="$t('AGENT_MGMT.PAUSE_SELF.END')"
              :is-loading="isPauseActionLoading"
              @click="stopPause"
            />
          </div>

          <div
            v-else-if="pauseState.pending_pause"
            class="rounded-xl border border-n-weak p-3"
          >
            <div class="text-sm font-medium text-n-slate-12">
              {{ pauseState.pending_pause.label }}
            </div>
            <div class="mt-1 text-sm text-n-slate-11">
              {{ $t('AGENT_MGMT.PAUSE_SELF.PENDING') }}
            </div>
            <Button
              class="mt-3"
              size="sm"
              slate
              :label="$t('AGENT_MGMT.PAUSE_SELF.CANCEL_REQUEST')"
              :is-loading="isPauseActionLoading"
              @click="stopPause"
            />
          </div>

          <div
            v-else-if="pauseState.legacy_pause"
            class="rounded-xl border border-n-weak p-3 text-sm text-n-slate-11"
          >
            {{
              $t('AGENT_MGMT.PAUSE_SELF.LEGACY_ACTIVE', {
                label: pauseState.legacy_pause.label,
              })
            }}
          </div>

          <div
            v-else-if="!pauseState.templates.length"
            class="text-sm text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.PAUSE_SELF.EMPTY') }}
          </div>

          <div v-else class="flex flex-col gap-2">
            <Button
              v-for="template in pauseState.templates"
              :key="template.id"
              class="justify-between"
              size="sm"
              slate
              :disabled="!template.can_request"
              :is-loading="isPauseActionLoading"
              @click="requestPause(template.id)"
            >
              <div class="flex flex-col items-start">
                <span>{{ template.name }}</span>
                <span class="text-xs text-n-slate-11">
                  {{
                    $t('AGENT_MGMT.PAUSE_SELF.TEMPLATE_META', {
                      duration: template.duration_in_minutes,
                      remaining: template.remaining_uses,
                    })
                  }}
                </span>
              </div>
            </Button>
          </div>
        </div>
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
